// @flow

import { LOCATION_CHANGE } from "connected-react-router";
import { startRequest, finishRequest } from "@store/utils";

import {
  GetComplianceTrendsDefaultReports,
  GetComplianceTrendsDefaultReportsSuccess,
  GetComplianceTrendsDefaultReportsError,
  GetComplianceTrendsMyReports,
  GetComplianceTrendsMyReportsSuccess,
  GetComplianceTrendsMyReportsError,
  GetComplianceTrendsReportData,
  GetComplianceTrendsReportDataSuccess,
  GetComplianceTrendsReportDataError,
  GetComplianceTrendsReportSchemaSuccess,
  GetComplianceTrendsReportSchemaError,
  SetComplianceTrendsReportToSave,
  ReSetComplianceTrendsReportToSave,
  SetComplianceReportViewState,
  ExportComplianceTrendsReport,
  ExportComplianceTrendsReportSuccess,
} from "@modules/reporting/constants/actions";
import type { ComplianceReportViewState, ComplianceState } from "./types";
import { cloneObject } from "@utils/object";

export const initialViewState: ComplianceReportViewState = {
  filtersExpanded: false,
  chartsExpanded: true,
  reportTableExpanded: true,
  activeFilter: "ALL",
  showFractions: false,
  showTotals: false,
};

export const initialState: ComplianceState = {
  error: null,
  requestsInProgress: 0,
  reportGenerateInProgress: 0,
  defaultReports: [],
  myReports: [],
  report: {
    details: null,
    reportData: {
      details: [],
      headlines: null,
    },
  },
  schema: null,
  reportToSave: null,
  viewState: initialViewState,
};

export const setLoading = (state: ComplianceState) => () => startRequest(state);

export const onError = (state: ComplianceState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetComplianceTrendsDefaultReportsSuccess = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    defaultReports: payload,
  };
};

export const onGetComplianceTrendsMyReportsSuccess = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    myReports: payload,
  };
};

export const onGetComplianceTrendsReportDataSuccess = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    ...finishRequest(state),
    error: null,
    report: payload,
  };
};

export const onGetComplianceTrendsReportSchemaSuccess = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    schema: payload,
  };
};

export const onGetComplianceTrendsReportSchemaError = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    error: payload,
  };
};

export const onSetComplianceTrendsReportToSave = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    reportToSave: payload,
  };
};

export const onReSetComplianceTrendsReportToSave = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    reportToSave: null,
  };
};

export const onSetViewState = (state: ComplianceState) => ({ payload }: Object) => {
  return {
    ...state,
    viewState: payload,
  };
};

export const onResetViewState = (state: ComplianceState) => () => ({
  ...state,
  viewState: initialViewState,
});

export const onExportComplianceTrendsReport = (state: ComplianceState) => () => ({
  ...state,
  reportGenerateInProgress: ++state.reportGenerateInProgress,
});

export const onExportComplianceTrendsReportSuccess = (state: ComplianceState) => () => ({
  ...state,
  reportGenerateInProgress:
    state.reportGenerateInProgress > 0 ? --state.reportGenerateInProgress : 0,
});

export default (state: ComplianceState = initialState, action: Object): ComplianceState => {
  const act = {
    [ExportComplianceTrendsReport]: onExportComplianceTrendsReport,
    [ExportComplianceTrendsReportSuccess]: onExportComplianceTrendsReportSuccess,
    [GetComplianceTrendsDefaultReports]: setLoading,
    [GetComplianceTrendsDefaultReportsSuccess]: onGetComplianceTrendsDefaultReportsSuccess,
    [GetComplianceTrendsDefaultReportsError]: onError,
    [GetComplianceTrendsMyReports]: setLoading,
    [GetComplianceTrendsMyReportsSuccess]: onGetComplianceTrendsMyReportsSuccess,
    [GetComplianceTrendsMyReportsError]: onError,
    [GetComplianceTrendsReportData]: setLoading,
    [GetComplianceTrendsReportDataSuccess]: onGetComplianceTrendsReportDataSuccess,
    [GetComplianceTrendsReportDataError]: onError,
    [GetComplianceTrendsReportSchemaSuccess]: onGetComplianceTrendsReportSchemaSuccess,
    [GetComplianceTrendsReportSchemaError]: onGetComplianceTrendsReportSchemaError,
    [SetComplianceTrendsReportToSave]: onSetComplianceTrendsReportToSave,
    [ReSetComplianceTrendsReportToSave]: onReSetComplianceTrendsReportToSave,
    [SetComplianceReportViewState]: onSetViewState,
    [LOCATION_CHANGE]: onResetViewState,
  };

  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
